/* =============================================================================
   LocaleService - an OPEN locale registry: adding a locale is one register()
   call, and a descriptor may be extended in place by registering it again.
   Shipped set: en = base and fallback, bn = active, ja and tr = registered but
   inactive until their catalogs and fonts are authored.
   Rules that hold for the active set: Western digits, Western 1,234,567 grouping
   (never lakh/crore), dd MMM yyyy as the canonical date form.
   Locale-specific branches (Turkish dotted/dotless-I case folding, full-width
   digit normalisation) stay DORMANT until that locale is the active one, so
   activating a locale is the only thing that can change formatting behaviour.
   API: register(id, descriptor) · active · set(id) · descriptor(id) · fold(str) ·
   normalizeDigits(str). set() with an unregistered id warns and does nothing.
   ============================================================================= */
import { env } from '../core/env.js';

const _locales = new Map();
let _active = 'en';

export const LocaleService = {
  /** register a locale descriptor, or extend one already registered */
  register(id, descriptor) {
    _locales.set(id, Object.assign(_locales.get(id) || {}, descriptor));
  },

  get active() { return _active; },

  /** switch locale: font roles and catalogs follow, and <html lang> is rewritten */
  set(id) {
    if (!_locales.has(id)) { console.warn('[ics] LocaleService: unknown locale', id); return; }
    _active = id;
    document.documentElement.lang = id;
  },

  descriptor(id) { return _locales.get(id || _active) || _locales.get('en'); },

  /**
   * Case-fold for case-insensitive matching (autocomplete, grid search, title
   * match). Plain toLowerCase for en/bn/ja; the Turkish dotted/dotless-I branch
   * activates ONLY while tr is the active locale.
   */
  fold(str) {
    return _active === 'tr' ? String(str).toLocaleLowerCase('tr') : String(str).toLowerCase();
  },

  /**
   * Digit-normalisation seam (full-width digit forms). Identity for the active
   * locale set; a locale that needs a real mapping authors its branch here.
   */
  normalizeDigits(str) {
    return String(str);
  },
};

/* ---- shipped descriptors --------------------------------------------------- */
LocaleService.register('en', {
  name: 'English', status: 'base', fontRole: '--ics-font-latin',
  group: ',', decimal: '.', dateCanonical: 'dd MMM yyyy', catalog: 'en',
});
LocaleService.register('bn', {
  name: 'Bangla (Bangladesh)', status: 'active', fontRole: '--ics-font-bengali-default',
  group: ',', decimal: '.',                     /* Western digits + Western grouping */
  dateCanonical: 'dd MMM yyyy', catalog: 'bn',
  legacyAnsiFontRole: '--ics-font-legacy-ansi', /* ANSI-encoded text: byte-preserving, never normalize */
});
LocaleService.register('ja', { name: 'Japanese', status: 'registered-inactive', fontRole: '--ics-font-japanese', catalog: 'ja' });
LocaleService.register('tr', { name: 'Turkish', status: 'registered-inactive', fontRole: '--ics-font-latin', catalog: 'tr' });

// boot value from <html lang> (core/env.js); an unregistered value falls back to en silently
if (_locales.has(env.locale)) _active = env.locale;
